/**
 * Tip Service (symbolic "tip the DJ" points — no money involved)
 *
 * A tip is a single appreciation point. Each tip is written as a document in
 * the top-level `tips` collection (guests can create these, exactly like
 * `crowdmood`), and the two counters shown in the UI are derived with cheap
 * server-side COUNT aggregation rather than by reading every tip doc.
 *
 * Credit rule: a tip on a venue channel credits the LIVE guest DJ if one is on
 * air (users/{venueUid}.liveGuestDj), otherwise the channel owner. "Tonight"
 * = tips dated today; "All time" = every tip that DJ has ever received.
 *
 * Firestore prerequisites (add these — they can't be created from client code):
 *   Rules:  match /tips/{id} { allow create: if request.auth != null;
 *                              allow read:   if request.auth != null; }
 *   Indexes: tips(toUid ASC)  and  tips(toUid ASC, date ASC)
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getCountFromServer,
  doc,
  getDoc,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase-config.js';

export const tipTodayKey = () => new Date().toISOString().split('T')[0];

/**
 * Who should a tip on this venue channel credit? The live guest DJ if one is
 * on air, otherwise the channel owner (venue/DJ).
 */
export const resolveTipRecipient = async (venueUid) => {
  if (!venueUid) return null;
  try {
    const snap = await getDoc(doc(db, 'users', venueUid));
    return (snap.exists() && snap.data()?.liveGuestDj) || venueUid;
  } catch (_) {
    return venueUid;
  }
};

/**
 * Record one symbolic tip point.
 * @returns {Promise<{ok:boolean, recipient?:string}>}
 */
export const sendTip = async (venueUid, fromUid) => {
  const recipient = await resolveTipRecipient(venueUid);
  if (!recipient) return { ok: false };
  try {
    await addDoc(collection(db, 'tips'), {
      toUid: recipient, // the DJ/venue being tipped
      venueUid, // the channel the tip came through
      fromUid: fromUid || null, // the guest who tipped
      date: tipTodayKey(),
      at: serverTimestamp(),
    });
    return { ok: true, recipient };
  } catch (err) {
    console.error('[tipService] sendTip failed:', err);
    return { ok: false, recipient };
  }
};

/**
 * Has this guest already tipped this recipient today (i.e. this session)?
 * Used to enforce one tip per DJ per session.
 * (Needs a composite index: tips → toUid ASC, fromUid ASC, date ASC.)
 */
export const hasTippedToday = async (recipientUid, fromUid) => {
  if (!recipientUid || !fromUid) return false;
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tips'),
        where('toUid', '==', recipientUid),
        where('fromUid', '==', fromUid),
        where('date', '==', tipTodayKey()),
        limit(1)
      )
    );
    return !snap.empty;
  } catch (err) {
    console.warn('[tipService] hasTippedToday failed:', err);
    return false;
  }
};

/**
 * Read a recipient's tip counters (cheap COUNT aggregation, no doc reads).
 * @returns {Promise<{allTime:number, tonight:number}>}
 */
export const getTipTotals = async (recipientUid) => {
  if (!recipientUid) return { allTime: 0, tonight: 0 };
  const base = collection(db, 'tips');
  try {
    const [allSnap, todaySnap] = await Promise.all([
      getCountFromServer(query(base, where('toUid', '==', recipientUid))),
      getCountFromServer(
        query(base, where('toUid', '==', recipientUid), where('date', '==', tipTodayKey()))
      ),
    ]);
    return {
      allTime: allSnap.data().count || 0,
      tonight: todaySnap.data().count || 0,
    };
  } catch (err) {
    console.warn('[tipService] getTipTotals failed:', err);
    return { allTime: 0, tonight: 0 };
  }
};
